// ATN-01 · M6 — resultado EXPLÍCITO del guardado y operación ESTABLE por (caso, actor, control).
// Helper local y pequeño: evita duplicar el estado de operaciones en cada callback de la consola.
// No persiste nada (sin localStorage, sin IndexedDB, sin cola) y no reintenta por su cuenta.
//
// REPARACIÓN M6-A/E — se distinguen DOS cosas que antes se confundían:
//   · EN CURSO  : hay una petición en vuelo ahora mismo (evita el doble envío del mismo tick).
//   · PENDIENTE : la operación NO tiene resolución acreditada. Su identidad (op_id + payload) sigue
//                 VIVA, así que un reintento manual reutiliza exactamente la misma clave de
//                 idempotencia y el mismo payload, en vez de crear otra acción idéntica.
// La identidad pendiente sólo se suelta con resolución acreditada o con un descarte explícito.

export const RESULTADO = {
  RECHAZADO: "rechazado",
  INCIERTO: "incierto",
  CONFIRMADO: "confirmado",
  CONFIRMADO_REFRESCO_PENDIENTE: "confirmado_refresco_pendiente",
};

// Un error de PROXY genérico no acredita rechazo de la mutación: sólo una respuesta identificada
// de la API lo hace. Timeout, red, respuesta perdida y 5xx quedan INCIERTOS.
export function clasificarFallo(e) {
  const r = e?.response;
  if (!r) return RESULTADO.INCIERTO;                 // red, timeout, respuesta perdida
  const s = r.status;
  if (s >= 500 || s === 502 || s === 503 || s === 504) return RESULTADO.INCIERTO;
  if (s === 408 || s === 429) return RESULTADO.INCIERTO;
  if (r.data === undefined || r.data === null || r.data === "") return RESULTADO.INCIERTO;
  return RESULTADO.RECHAZADO;                        // respuesta identificada de la API
}

const mismaCarga = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function crearRegistroOperaciones() {
  const enCurso = new Map();      // clave -> op con petición en vuelo
  const pendientes = new Map();   // clave -> op sin resolución acreditada (identidad viva)

  // BLOQUEO SINCRÓNICO: se adquiere ANTES de crear el op_id y ANTES de cualquier await, así dos
  // invocaciones del mismo envío en el mismo tick no crean dos operaciones distintas.
  const adquirir = (clave, datos = {}) => {
    const vuelo = enCurso.get(clave);
    if (vuelo) return { nuevo: false, enVuelo: true, op: vuelo };
    const prev = pendientes.get(clave);
    if (prev && !mismaCarga(prev.payload, datos.payload)) {
      // Otra atención distinta sobre el mismo control mientras la anterior sigue sin resolverse:
      // el payload de una operación pendiente es INMUTABLE, así que no se reutiliza su clave.
      return { nuevo: false, conflicto: true, op: prev };
    }
    const op = prev
      ? { ...prev, intentos: (prev.intentos || 1) + 1 }
      : {
        op_id: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID() : `op-${Date.now()}-${Math.random()}`,
        clave, intentos: 1, ...datos,
      };
    enCurso.set(clave, op);
    pendientes.set(clave, op);
    return { nuevo: true, reintento: !!prev, op };
  };

  return {
    adquirir,
    // La petición terminó sin acreditar nada: la identidad SIGUE pendiente y reutilizable.
    dejarPendiente: (clave) => { enCurso.delete(clave); },
    // Resolución acreditada (confirmada o rechazada por la API) o descarte humano explícito.
    resolver: (clave) => { enCurso.delete(clave); pendientes.delete(clave); },
    pendiente: (clave) => pendientes.get(clave) || null,
    enVuelo: (clave) => enCurso.has(clave),
    listaPendientes: () => Array.from(pendientes.values()),
    // Logout, cambio de actor o 401: el estado efímero de la sesión terminada se descarta.
    limpiarTodo: () => { enCurso.clear(); pendientes.clear(); },
  };
}

export const claveOperacion = (casoId, actorId, control) => `${casoId}::${actorId}::${control}`;
